import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    DollarSign,
    MonitorSmartphone,
    Scale,
    Zap,
    HeadphonesIcon,
    Percent,
} from 'lucide-react';

const advantages = [
    {
        icon: DollarSign,
        title: 'Capital Up to $100K',
        description:
            'Trade with up to $100,000 in funded capital without risking your own money. Focus on what you do best while we provide the resources.',
    },
    {
        icon: MonitorSmartphone,
        title: `${siteConfig.platform.name} Platform`,
        description:
            `Trade on one of the most advanced platforms in the industry. ${siteConfig.platform.name} offers institutional-grade charting, ultra-fast execution, and cross-device compatibility.`,
    },
    {
        icon: Scale,
        title: 'Fair & Transparent Rules',
        description:
            'No hidden conditions or surprise violations. Our trading rules are transparent, straightforward, and designed to let skilled traders thrive.',
    },
    {
        icon: Zap,
        title: 'Fast Payouts',
        description:
            'Request your profits and receive them quickly. We support multiple payment methods including bank transfers and cryptocurrency for global accessibility.',
    },
    {
        icon: HeadphonesIcon,
        title: '24/7 Support',
        description:
            'Our dedicated support team is available around the clock to assist you. Get personalized help via live chat, email, or phone whenever you need it.',
    },
    {
        icon: Percent,
        title: '80% Profit Split',
        description:
            'Keep 80% of the profits you earn. Our generous profit split ensures that your trading success translates directly into real earnings.',
    },
];

export default function WhyTefFunded() {
    return (
        <AppLayout title={`Why ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Why Us</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Why Choose <span className="text-gradient">{siteConfig.brand.name}</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            We built a prop trading firm that puts traders first. From transparent rules
                            to lightning-fast payouts, discover what sets {siteConfig.brand.name} apart.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Key Advantages */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Key"
                        highlight="Advantages"
                        description="Everything you need to trade at the highest level, backed by a firm that genuinely supports your success."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {advantages.map((advantage) => {
                            const Icon = advantage.icon;
                            return (
                                <motion.div key={advantage.title} variants={staggerItem}>
                                    <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                                <Icon className="h-6 w-6 text-primary" />
                                            </div>
                                            <h3 className="text-lg font-bold text-foreground mb-2">
                                                {advantage.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {advantage.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
                            Ready to Trade with the <span className="text-gradient">Best?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Join thousands of traders who chose {siteConfig.brand.name} for transparent rules, fast
                            payouts, and world-class trading infrastructure.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Funded Today
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button variant="outline" size="lg">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
